/**
 * Prominent banner shown above the bank-accounts list when Teller is in
 * sandbox mode. Explains the sandbox-only test credentials users must use —
 * entering real credentials leaves Teller spinning forever (user confusion
 * fixed by this banner).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Info, Copy, Check } from "lucide-react";

export default function TellerSandboxBanner() {
  const [config, setConfig] = useState(null);
  const [copied, setCopied] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return window.localStorage.getItem("ll:teller:sandbox:dismissed") === "1"; } catch { return false; }
  });

  useEffect(() => {
    api.get("/bank-connect/config")
      .then((r) => setConfig(r.data))
      .catch(() => {});
  }, []);

  if (!config?.configured) return null;
  if ((config.environment || "sandbox") !== "sandbox") return null;
  if (dismissed) return null;

  const copy = (label, value) => {
    try { navigator.clipboard.writeText(value); setCopied(label); setTimeout(() => setCopied(null), 1500); } catch {}
  };
  const dismiss = () => {
    setDismissed(true);
    try { window.localStorage.setItem("ll:teller:sandbox:dismissed", "1"); } catch {}
  };

  return (
    <div
      className="border-l-4 p-4 mb-6 bg-[#FFFBEB]"
      style={{ borderColor: "#F59E0B" }}
      data-testid="teller-sandbox-banner"
    >
      <div className="flex items-start gap-3">
        <Info size={18} className="text-[#B45309] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base text-[#78350F] mb-1">
            Teller is running in sandbox mode — real banks won&apos;t work yet
          </div>
          <p className="text-sm text-[#78350F] leading-relaxed">
            Teller sandbox simulates banks; entering your real bank credentials makes
            Teller&apos;s login screen spin indefinitely. To test the flow, pick any
            bank in Teller Connect and sign in with the sandbox credentials:
          </p>

          <div className="mt-3 flex flex-wrap gap-2" data-testid="teller-sandbox-creds">
            <button
              type="button"
              onClick={() => copy("username", "username")}
              data-testid="teller-sandbox-copy-username"
              className="font-mono-fin text-sm px-3 py-1.5 bg-white border border-[#F59E0B] hover:bg-[#FEF3C7] flex items-center gap-2"
            >
              <span className="text-[11px] uppercase tracking-widest text-[#B45309]">Username</span>
              <span className="font-semibold">username</span>
              {copied === "username" ? <Check size={12} className="text-[#059669]" /> : <Copy size={12} className="opacity-50" />}
            </button>
            <button
              type="button"
              onClick={() => copy("password", "password")}
              data-testid="teller-sandbox-copy-password"
              className="font-mono-fin text-sm px-3 py-1.5 bg-white border border-[#F59E0B] hover:bg-[#FEF3C7] flex items-center gap-2"
            >
              <span className="text-[11px] uppercase tracking-widest text-[#B45309]">Password</span>
              <span className="font-semibold">password</span>
              {copied === "password" ? <Check size={12} className="text-[#059669]" /> : <Copy size={12} className="opacity-50" />}
            </button>
          </div>

          <p className="text-xs text-[#78350F] mt-3 opacity-90">
            <strong>Ready to connect real banks?</strong> Ask an admin to set
            <code className="mx-1 px-1 bg-white border border-[#F59E0B]">TELLER_ENVIRONMENT=development</code>
            in <code className="px-1 bg-white border border-[#F59E0B]">backend/.env</code> and restart.
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          data-testid="teller-sandbox-dismiss"
          className="text-xs text-[#B45309] hover:text-[#78350F] underline shrink-0"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
